/**
 * Action handlers for Smootify-style buttons + Webflow product forms.
 *
 * ## Pourquoi 2 niveaux d'interception
 *
 * Smootify wrappe le bouton "Ajouter au panier" dans un <form> Webflow natif
 * (classe `w-form`). Webflow attache son propre handler `submit` sur ces forms
 * et affiche par défaut « Thank you! Your submission has been received! » dès
 * qu'un submit bubble. Pour gagner devant ce handler il faut :
 *
 *   1. Intercepter `submit` en phase CAPTURE (capture=true) sur document, AVANT
 *      que l'event ne descende vers le form.
 *   2. Appeler `ev.preventDefault()` ET `ev.stopPropagation()` — sans le stop,
 *      le handler jQuery Webflow pourrait encore voir l'event.
 *   3. Déterminer l'action (add-to-cart / buy-now) depuis le submitter si
 *      disponible (le bouton cliqué), sinon chercher dans le form.
 *
 * En plus, on garde un click handler sur [action] pour les boutons qui ne sont
 * PAS dans un <form> (ex: card produit dans une listing collection).
 *
 * ## Supported attributes
 *
 *   - [action="add-to-cart"] → addToCart(variant, 1)
 *   - [action="buy-now"]     → addToCart(variant, 1, goToCheckout=true)
 *   - [action="/search"]     → laissé au browser (form submit natif)
 *   - <button type="submit"> sans [action] DANS un <smootify-product>
 *       → assumé add-to-cart (compat Smootify où on peut avoir juste un button
 *         submit sans attribut explicite)
 */

import { addToCart } from "../cart/state.js";
import type { Cart, Product, ProductVariant } from "../types.js";
import { manageCommerceForm, setCommerceBusy, showCommerceError, syncProductFormButtons } from "../ui/commerce-form.js";

// ---------------------------------------------------------------------------
//  Host resolution
// ---------------------------------------------------------------------------

interface HostSnapshot {
  host: HTMLElement | null;
  product: Product | null;
  variant: ProductVariant | null;
}

interface SmootifyProductLike extends HTMLElement {
  product?: Product | null;
  selectedVariant?: ProductVariant | null;
}

function findHost(el: HTMLElement): SmootifyProductLike | null {
  return el.closest<SmootifyProductLike>("smootify-product");
}

function snapshot(el: HTMLElement): HostSnapshot {
  const host = findHost(el);
  if (!host) return { host: null, product: null, variant: null };
  return {
    host,
    product: host.product ?? null,
    variant: host.selectedVariant ?? null,
  };
}

// ---------------------------------------------------------------------------
//  Core : run addToCart
// ---------------------------------------------------------------------------

async function runAddToCart(
  el: HTMLElement,
  goToCheckout: boolean,
): Promise<Cart | null> {
  const scope = el.closest("form") ?? el;
  if (scope.getAttribute("aria-busy") === "true" || el.getAttribute("data-state") === "loading") return null;
  showCommerceError(el, null);
  const snap = snapshot(el);
  const { host, product, variant } = snap;

  // Debug visible : on log explicitement pourquoi on échoue, sinon le clic
  // semble "ne rien faire". Un user peut copier-paste la console à l'équipe.
  if (!host) {
    showCommerceError(el, "Cet article ne peut pas être ajouté pour le moment. Rechargez la page et réessayez.");
    // eslint-disable-next-line no-console
    console.warn(
      "[lmdr] addToCart aborted — no <smootify-product> ancestor found on element",
      el,
      ". Vérifie que le bouton 'Ajouter au panier' est placé dans un <smootify-product> sur Webflow.",
    );
    document.dispatchEvent(
      new CustomEvent("smootify:error", {
        detail: { type: "add_to_cart", reason: "no_host", element: el },
      }),
    );
    return null;
  }
  if (!product) {
    showCommerceError(el, "Le produit est encore en cours de chargement. Réessayez dans un instant.");
    // eslint-disable-next-line no-console
    console.warn(
      "[lmdr] addToCart aborted — <smootify-product> found but product not loaded yet.",
      host,
    );
    return null;
  }
  if (!variant) {
    showCommerceError(el, "Sélectionnez une variante disponible avant d’ajouter cet article.");
    // eslint-disable-next-line no-console
    console.warn(
      "[lmdr] addToCart aborted — aucune variante sélectionnée. Produit:",
      product.handle,
      ". Vérifie le variant-selector.",
    );
    document.dispatchEvent(
      new CustomEvent("smootify:error", {
        detail: { type: "add_to_cart", reason: "no_variant", product },
      }),
    );
    return null;
  }
  if (!variant.availableForSale) {
    showCommerceError(el, "Cette variante est actuellement indisponible.");
    return null;
  }

  const quantity = readQuantity(el);

  setCommerceBusy(el, true);
  try {
    const cart = await addToCart(
      [{ merchandiseId: variant.id, quantity }],
      goToCheckout,
    );
    if (!cart) showCommerceError(el, "L’ajout n’a pas abouti. Réessayez dans un instant.");

    // addToCart gère la redirection quand goToCheckout=true ET URL existe,
    // mais on fallback au cas où.
    if (goToCheckout && cart?.checkoutUrl && typeof window !== "undefined") {
      if (window.location.href !== cart.checkoutUrl) {
        window.location.href = cart.checkoutUrl;
      }
    }
    return cart;
  } catch (err) {
    showCommerceError(el, "Impossible d’ajouter cet article. Vérifiez votre connexion et réessayez.");
    // eslint-disable-next-line no-console
    console.error(
      "[lmdr] addToCart failed (Shopify Storefront API):",
      (err as Error).message,
      err,
    );
    document.dispatchEvent(
      new CustomEvent("smootify:error", {
        detail: { type: "add_to_cart", reason: "api", message: (err as Error).message },
      }),
    );
    return null;
  } finally {
    setCommerceBusy(el, false);
  }
}

function readQuantity(anchor: HTMLElement): number {
  const host = anchor.closest<HTMLElement>("form") ?? findHost(anchor) ?? document;
  const input = host.querySelector<HTMLInputElement>(
    'input[name="quantity"], quantity-input input[type="number"], [data-action="quantity"], input[type="number"]',
  );
  if (!input) return 1;
  const n = Number.parseInt(input.value, 10);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

// ---------------------------------------------------------------------------
//  Classification : is this form a product/cart form?
// ---------------------------------------------------------------------------

/**
 * Détermine si un form Webflow doit être traité comme un add-to-cart.
 *
 * Heuristique (tout OR) :
 *   - Le form est DESCENDANT d'un <smootify-product>
 *   - Le form contient un [action="add-to-cart"] ou [action="buy-now"]
 *   - Le form contient un <variant-selector> / [data-option]
 *
 * Les forms de contact / newsletter / search ne matchent pas → Webflow gère.
 */
function isProductForm(form: HTMLFormElement): boolean {
  if (form.closest("smootify-product")) return true;
  if (form.querySelector('[action="add-to-cart"], [action="buy-now"]')) return true;
  if (form.querySelector("variant-selector, variant-dropdown, variant-swatches, [data-option]")) {
    return true;
  }
  return false;
}

/**
 * Détermine l'action à exécuter depuis le submitter (si bouton spécifique)
 * ou le form. Fallback : "add-to-cart".
 */
function resolveFormAction(form: HTMLFormElement, submitter: HTMLElement | null): "add-to-cart" | "buy-now" {
  const explicitSubmitter = submitter?.getAttribute("action");
  if (explicitSubmitter === "buy-now" || explicitSubmitter === "add-to-cart") {
    return explicitSubmitter;
  }
  // Certains designs Smootify mettent name="buy-now" sur le bouton.
  if (submitter?.getAttribute("name") === "buy-now") return "buy-now";

  const withBuy = form.querySelector<HTMLElement>('[action="buy-now"]');
  const withAdd = form.querySelector<HTMLElement>('[action="add-to-cart"]');
  // Si le form a seulement un bouton buy-now, on privilégie celui-ci ; sinon
  // add-to-cart par défaut.
  if (!withAdd && withBuy) return "buy-now";
  return "add-to-cart";
}

// ---------------------------------------------------------------------------
//  Event handlers
// ---------------------------------------------------------------------------

function onSubmit(ev: Event): void {
  const target = ev.target as HTMLElement | null;
  if (!target || target.tagName !== "FORM") return;
  const form = target as HTMLFormElement;

  // Si le form est wrappé par un <smootify-add-to-cart>, c'est lui qui gère
  // (meilleur UX : toggle des .button-loading-state Webflow). On ne fait rien
  // ici — le custom element a déjà attaché son propre listener capture sur
  // le form lui-même et va preventDefault avant qu'on arrive au bubble.
  if (form.closest("smootify-add-to-cart")) return;

  if (!isProductForm(form)) return; // Laisse Webflow gérer contact/newsletter/etc.

  // eslint-disable-next-line no-console
  console.log("[lmdr] intercept form submit → add-to-cart (document-level)", form);

  // Dedup : si un submit est déjà en cours sur ce form, on ignore.
  if (form.getAttribute("data-state") === "loading") {
    ev.preventDefault();
    ev.stopPropagation();
    ev.stopImmediatePropagation();
    return;
  }

  // On STOP le handler Webflow AVANT qu'il affiche "Thank you".
  ev.preventDefault();
  ev.stopPropagation();
  ev.stopImmediatePropagation();

  const submitter = ((ev as SubmitEvent).submitter as HTMLElement | null) ?? null;
  const action = resolveFormAction(form, submitter);
  const goToCheckout = action === "buy-now";

  const anchor = submitter ?? form;
  void runAddToCart(anchor, goToCheckout);
}

function onClick(ev: Event): void {
  const target = ev.target as HTMLElement | null;
  if (!target) return;
  const el = target.closest<HTMLElement>("[action]");
  if (!el) return;

  // Si le bouton est DANS un form, on laisse le form submit handler faire le
  // travail (sinon on ferait 2 runAddToCart). Le click → submit → onSubmit.
  if (el.closest("form")) return;

  const action = el.getAttribute("action") ?? "";

  // Dedup : si un click précédent est déjà en cours (data-state="loading"),
  // on ignore les clicks suivants. Évite les doubles ajouts.
  if (
    (action === "add-to-cart" || action === "buy-now") &&
    el.getAttribute("data-state") === "loading"
  ) {
    ev.preventDefault();
    return;
  }

  if (action === "add-to-cart") {
    ev.preventDefault();
    void runAddToCart(el, false);
    return;
  }

  if (action === "buy-now") {
    ev.preventDefault();
    void runAddToCart(el, true);
    return;
  }

  // "/search" et autres → laisse le browser / Webflow gérer.
}

// ---------------------------------------------------------------------------
//  Public
// ---------------------------------------------------------------------------

interface DocumentWithFlag extends Document {
  __lmdrActionsAttached?: boolean;
  __lmdrSubmitAttached?: boolean;
}

/**
 * Attach les handlers globaux (submit + click) exactement 1 fois. Idempotent.
 *
 * Le submit listener est en CAPTURE=true pour passer devant le handler
 * Webflow `w-form` qui bubble par défaut.
 */
export function attachActionHandlers(root: ParentNode = document): void {
  const doc = typeof document !== "undefined" ? (document as DocumentWithFlag) : null;

  // Claim forms before Webflow gets another opportunity to initialize them.
  root.querySelectorAll<HTMLFormElement>("form").forEach((form) => {
    if (!isProductForm(form)) return;
    manageCommerceForm(form);
    syncProductFormButtons(form);
    // Product hydration sets data-state after assigning its product/variant.
    // One observer per host also handles the initial asynchronous load.
    if (!form.closest("smootify-add-to-cart")) observeProductForm(form);
  });

  if (root === doc) {
    if (!doc) return;
    if (!doc.__lmdrSubmitAttached) {
      doc.__lmdrSubmitAttached = true;
      // capture=true : on intercepte AVANT que l'event ne descende vers le form
      // et son handler Webflow natif.
      doc.addEventListener("submit", onSubmit, true);
    }
    if (!doc.__lmdrActionsAttached) {
      doc.__lmdrActionsAttached = true;
      doc.addEventListener("click", onClick);
    }
    return;
  }

  // Scoped attach — utilisé pour un sous-arbre injecté dynamiquement.
  const target = root as unknown as EventTarget;
  if (scopedTargets.has(target)) return;
  scopedTargets.add(target);
  target.addEventListener("submit", onSubmit, true);
  target.addEventListener("click", onClick);
}

const observedForms = new WeakSet<HTMLFormElement>();
const scopedTargets = new WeakSet<EventTarget>();
function observeProductForm(form: HTMLFormElement): void {
  if (observedForms.has(form)) return;
  observedForms.add(form);
  const host = findHost(form);
  if (!host) return;
  const sync = (): void => syncProductFormButtons(form);
  host.addEventListener("smootify:variant_changed", sync);
  new MutationObserver(sync).observe(host, { attributes: true, attributeFilter: ["data-state"] });
}

export { runAddToCart };
